import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AppShell } from "@/components/layout/AppShell";
import { EstimadoVsEmitidoCard } from "@/components/oc/EstimadoVsEmitidoCard";
import { BiFilterBar, useBiFilters } from "@/components/bi/BiFilters";
import {
  CHART_COLORS,
  ChartCard,
  ChartState,
  MetricCard,
  formatMoneyCLP,
  formatMoneyMap,
  formatNumber,
  tooltipStyle,
} from "@/components/bi/BiPrimitives";
import { canalLabel, estadoMPLabel } from "@/lib/mercado-publico-catalog";
import { cn } from "@/lib/utils";
import { biAnalyticsService } from "@/services/biAnalytics.service";

export const Route = createFileRoute("/bi/mercado")({
  head: () => ({ meta: [{ title: "Reporte Mercado Público — LicitaPro" }] }),
  component: Page,
});

const DOW = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

function Page() {
  const { filters } = useBiFilters();
  const kpis = useQuery({
    queryKey: ["bi", "kpis", filters],
    queryFn: () => biAnalyticsService.kpis(filters),
  });
  const trends = useQuery({
    queryKey: ["bi", "trends", filters],
    queryFn: () => biAnalyticsService.trends(filters),
  });
  const estados = useQuery({
    queryKey: ["bi", "breakdown", "estadoMP", filters],
    queryFn: () => biAnalyticsService.breakdown(filters, "estadoMP"),
  });
  const canales = useQuery({
    queryKey: ["bi", "breakdown", "canal", filters],
    queryFn: () => biAnalyticsService.breakdown(filters, "canal"),
  });
  const heatmap = useQuery({
    queryKey: ["bi", "heatmap", "publicacion", filters],
    queryFn: () => biAnalyticsService.heatmap(filters, "publicacion"),
  });

  const k = kpis.data;
  const maxCell = Math.max(1, ...(heatmap.data ?? []).map((c) => c.count));

  return (
    <AppShell
      title="Mercado Público"
      subtitle="Demanda publicada en ChileCompra según los filtros seleccionados"
      actions={
        <Link
          to="/bi"
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition"
        >
          <ArrowLeft className="size-3.5" /> Reportes
        </Link>
      }
    >
      <BiFilterBar />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <MetricCard
          label="Publicadas"
          value={formatNumber(k?.published ?? 0)}
          loading={kpis.isLoading}
        />
        <MetricCard
          label="Vigentes"
          value={formatNumber(k?.active ?? 0)}
          hint={`${formatNumber(k?.closingSoon ?? 0)} cierran en 7 días`}
          loading={kpis.isLoading}
        />
        <MetricCard
          label="Monto publicado"
          value={formatMoneyMap(k?.amounts ?? {})}
          loading={kpis.isLoading}
        />
        <MetricCard
          label="Ticket promedio (CLP)"
          value={k?.avgTicketCLP != null ? formatMoneyCLP(k.avgTicketCLP) : "—"}
          loading={kpis.isLoading}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <ChartCard
          title="Evolución de publicaciones"
          subtitle="Licitaciones publicadas y monto CLP por mes"
          className="xl:col-span-2"
        >
          <ChartState
            loading={trends.isLoading}
            error={trends.error}
            empty={!trends.data?.length}
            height={280}
          >
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trends.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                <XAxis
                  dataKey="bucket"
                  stroke="var(--color-muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="var(--color-muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Area
                  type="monotone"
                  dataKey="published"
                  name="Publicadas"
                  stroke="var(--color-chart-1)"
                  fill="var(--color-chart-1)"
                  fillOpacity={0.2}
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="submitted"
                  name="Postuladas"
                  stroke="var(--color-chart-2)"
                  fill="var(--color-chart-2)"
                  fillOpacity={0.15}
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartState>
        </ChartCard>

        <ChartCard title="Estados MP" subtitle="Distribución según estado oficial">
          <ChartState
            loading={estados.isLoading}
            error={estados.error}
            empty={!estados.data?.length}
            height={280}
          >
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={(estados.data ?? []).map((r) => ({
                    ...r,
                    name: estadoMPLabel(r.name),
                  }))}
                  dataKey="count"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                >
                  {(estados.data ?? []).map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </ChartState>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
        <EstimadoVsEmitidoCard />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard title="Canales de compra" subtitle="Volumen por canal ChileCompra">
          <ChartState
            loading={canales.isLoading}
            error={canales.error}
            empty={!canales.data?.length}
          >
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={(canales.data ?? []).map((r) => ({ ...r, name: canalLabel(r.name) }))}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="var(--color-muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="var(--color-muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  width={130}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" name="Licitaciones" fill="var(--color-chart-1)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartState>
        </ChartCard>

        <ChartCard
          title="Mapa de calor de publicaciones"
          subtitle="Día de la semana × hora de publicación"
        >
          <ChartState
            loading={heatmap.isLoading}
            error={heatmap.error}
            empty={!heatmap.data?.length}
          >
            <div className="overflow-x-auto">
              <div className="min-w-[520px]">
                <div className="grid grid-cols-[40px_repeat(24,1fr)] gap-0.5 mb-1">
                  <span />
                  {Array.from({ length: 24 }, (_, h) => (
                    <span key={h} className="text-[9px] text-muted-foreground text-center">
                      {h % 3 === 0 ? h : ""}
                    </span>
                  ))}
                </div>
                {DOW.map((label, dow) => (
                  <div key={dow} className="grid grid-cols-[40px_repeat(24,1fr)] gap-0.5 mb-0.5">
                    <span className="text-[10px] text-muted-foreground self-center">{label}</span>
                    {Array.from({ length: 24 }, (_, hour) => {
                      const cell = heatmap.data?.find((c) => c.dow === dow && c.hour === hour);
                      const intensity = cell ? cell.count / maxCell : 0;
                      return (
                        <div
                          key={hour}
                          title={`${label} ${hour}:00 — ${cell?.count ?? 0} publicaciones`}
                          className={cn("h-5 rounded-[3px]", !cell && "bg-muted/40")}
                          style={
                            cell
                              ? {
                                  background: `color-mix(in srgb, var(--color-chart-1) ${Math.max(12, intensity * 100)}%, transparent)`,
                                }
                              : undefined
                          }
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </ChartState>
        </ChartCard>
      </div>
    </AppShell>
  );
}
